
import React, { useEffect, useState } from "react";
import { useAuth } from "../App";
import Layout from "../components/Layout";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Building2, Mail, Phone, MapPin, Clock, Edit2, DollarSign, Plus, Search, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-2xl relative shadow-lg overflow-y-auto max-h-[90vh]">
        <button className="absolute top-2 right-2 text-gray-500 hover:text-[#3F7373] text-lg font-bold transition" onClick={onClose}>✖</button>
        {children}
      </div>
    </div>
  );
}

const tarifLabels = {
  weekday_rate: "Tarif semaine",
  weekend_rate: "Tarif week-end",
  night_week_rate: "Tarif nuit en semaine",
  night_weekend_rate: "Tarif nuit week-end",
  travel_weekday_fee: "Déplacement - semaine",
  travel_weekend_fee: "Déplacement - week-end",
  travel_night_week_fee: "Déplacement - nuit en semaine",
  travel_night_weekend_fee: "Déplacement - nuit week-end"
};
const tarifKeys = Object.keys(tarifLabels);
const getTarifLabel = key => tarifLabels[key] || key.replace(/_/g, " ");
const getOriginalKey = key => `original_${key}`;
const getOriginalLabel = key => `${getTarifLabel(key)} originel`;
const emptyRoleTarification = () => tarifKeys.reduce((acc, key) => {
  acc[key] = 0;
  acc[getOriginalKey(key)] = 0;
  return acc;
}, {});
const normalizeRoleTarification = data => tarifKeys.reduce((acc, key) => {
  acc[key] = data?.[key] ?? 0;
  acc[getOriginalKey(key)] = data?.[getOriginalKey(key)] ?? 0;
  return acc;
}, {});
const rolePayload = data => tarifKeys.reduce((acc, key) => {
  acc[key] = parseFloat(data?.[key]) || 0;
  acc[getOriginalKey(key)] = parseFloat(data?.[getOriginalKey(key)]) || 0;
  return acc;
}, {});

export default function Institutions() {
  const { axiosInstance, user } = useAuth();
  const CHEF_USER_IDS = ["96eb59c8-8a0f-48fb-babb-62ea934fa604", "6a21b1ae22ae5c77adf02f9f"];
  const normalizeRole = value => String(value || "").trim().toLowerCase();
  const userRole = normalizeRole(user?.role);
  const userCareRole = normalizeRole(user?.care_role);
  const isAdmin = userRole === "admin";
  const isChef = userRole === "chef" || userRole === "chief" || userCareRole === "chef" || userCareRole === "chief" || user?.is_chef === true || user?.is_chief === true || CHEF_USER_IDS.includes(String(user?.id || "")) || CHEF_USER_IDS.includes(String(user?._id || ""));
  const canManage = isAdmin || isChef;
  const [institutions, setInstitutions] = useState([]);
  const [editingInst, setEditingInst] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [tarificationFilter, setTarificationFilter] = useState("all");

  const filteredInstitutions = institutions.filter(inst => {
    const search = searchTerm.trim().toLowerCase();
    const matchesSearch = !search || [
      inst.name,
      inst.email,
      inst.phone,
      inst.address
    ].some(value => String(value || "").toLowerCase().includes(search));

    const matchesStatus =
      statusFilter === "all" || String(inst.status || "").toLowerCase() === statusFilter;

    const matchesTarification =
      tarificationFilter === "all" ||
      String(inst.type_tarification || "normal").toLowerCase() === tarificationFilter;

    return matchesSearch && matchesStatus && matchesTarification;
  });

  const hasActiveFilters =
    searchTerm.trim() !== "" ||
    statusFilter !== "all" ||
    tarificationFilter !== "all";

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setTarificationFilter("all");
  };

  useEffect(() => { fetchInstitutions(); }, []);

  const fetchInstitutions = async () => {
    try {
      const res = await axiosInstance.get("/institutions");
      setInstitutions(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du chargement des établissements");
    }
  };

  const buildPayload = inst => ({
    name: inst.name,
    email: inst.email,
    phone: inst.phone,
    address: inst.address,
    type_tarification: inst.type_tarification || "normal",
    tarification: {
      infirmier: rolePayload(inst.tarification?.infirmier),
      aide_soignant: rolePayload(inst.tarification?.aide_soignant)
    }
  });

  const handleCreate = async () => {
    if (!canManage) {
      toast.error("Vous n'avez pas l'autorisation de créer un établissement.");
      return;
    }
    if (!editingInst) {
      toast.error("Aucune donnée d'établissement.");
      return;
    }
    try {
      const payload = {
        id: `inst_${Date.now()}`,
        ...buildPayload(editingInst),
        status: "pending",
        role: "établissement de santé"
      };
      const res = await axiosInstance.post("/institutions", payload);
      setInstitutions(prev => [res.data, ...prev]);
      setEditingInst(null);
      setIsCreating(false);
      toast.success("Nouvel établissement créé !");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleSave = async () => {
    if (!canManage) {
      toast.error("Vous n'avez pas l'autorisation de modifier les établissements.");
      return;
    }
    if (!editingInst) return;
    if (isCreating) {
      await handleCreate();
      return;
    }
    try {
      const id = editingInst.id || editingInst._id;
      const res = await axiosInstance.put(`/institutions/${id}`, buildPayload(editingInst));
      setInstitutions(prev => prev.map(inst => String(inst.id || inst._id) === String(id) ? res.data : inst));
      setEditingInst(null);
      setIsCreating(false);
      toast.success("Institution mise à jour !");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const openCreateModal = () => {
    if (!canManage) {
      toast.error("Vous n'avez pas l'autorisation de créer un établissement.");
      return;
    }
    setEditingInst({
      name: "",
      email: "",
      phone: "",
      address: "",
      type_tarification: "normal",
      tarification: { infirmier: emptyRoleTarification(), aide_soignant: emptyRoleTarification() }
    });
    setIsCreating(true);
  };

  const openEditModal = inst => {
    if (!canManage) {
      toast.error("Vous n'avez pas l'autorisation de modifier cet établissement.");
      return;
    }
    setIsCreating(false);
    setEditingInst({
      ...inst,
      tarification: {
        infirmier: normalizeRoleTarification(inst.tarification?.infirmier),
        aide_soignant: normalizeRoleTarification(inst.tarification?.aide_soignant)
      }
    });
  };

  const handleApprove = async institutionId => {
    if (!canManage) {
      toast.error("Vous n'avez pas l'autorisation d'approuver un établissement.");
      return;
    }
    try {
      const res = await axiosInstance.put(`/institutions/${institutionId}/approve`);
      setInstitutions(prev => prev.map(inst => String(inst.id || inst._id) === String(institutionId) ? res.data : inst));
      toast.success("Établissement approuvé");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'approbation");
    }
  };
  // 
  const handleDelete = async (inst) => {
    if (!canManage) {
      toast.error(
        "Vous n'avez pas l'autorisation de supprimer un établissement."
      );
      return;
    }

    const institutionId = inst.id || inst._id;
    const institutionName =
      inst.name || "cet établissement";

    const confirmed = window.confirm(
      `Supprimer définitivement "${institutionName}" ?\n\nCette action est irréversible.`
    );

    if (!confirmed) return;

    try {
      await axiosInstance.delete(
        `/institutions/${institutionId}`
      );

      setInstitutions((prev) =>
        prev.filter(
          (item) =>
            String(item.id || item._id) !==
            String(institutionId)
        )
      );

      toast.success(
        "Établissement supprimé avec succès."
      );
    } catch (error) {
      console.error(
        "Erreur suppression institution :",
        error
      );

      toast.error(
        error.response?.data?.detail ||
          "Erreur lors de la suppression de l'établissement"
      );
    }
  };

  const updateTarif = (role, key, value) => {
    setEditingInst(prev => ({
      ...prev,
      tarification: {
        ...prev.tarification,
        [role]: { ...prev.tarification?.[role], [key]: value }
      }
    }));
  };

  const renderTarifs = (inst, role) => (
    <ul className="space-y-1">
      {tarifKeys.map(key => (
        <li key={key}>
          {getTarifLabel(key)} : <strong>{inst.tarification?.[role]?.[key] ?? "—"} €</strong>
          {isAdmin && (
            <span className="block text-[11px] text-gray-400">
              {getOriginalLabel(key)} : <strong>{inst.tarification?.[role]?.[getOriginalKey(key)] ?? "—"} €</strong>
            </span>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <Layout title="Liste des établissements de santé">
      <div className="mb-8 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-[#3F7373]">Établissements</h1>
        {canManage && (
          <Button onClick={openCreateModal} className="bg-[#6FAFAF] hover:bg-[#4F8585] text-white flex items-center gap-2">
            <Plus size={20} />Créer un établissement
          </Button>
        )}
      </div>

      <div className="mb-5 rounded-lg border border-[#D4EBEB] bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs text-[#3F7373]">Rechercher un établissement</Label>
            <div className="relative mt-1">
              <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Nom, email, téléphone ou adresse..."
                className="pl-9"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-[#3F7373]">Statut</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="approved">Approuvés</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="rejected">Refusés</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-[#3F7373]">Tarification</Label>
            <Select value={tarificationFilter} onValueChange={setTarificationFilter}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="pourcentage">Pourcentage</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-gray-500">
            {filteredInstitutions.length} établissement{filteredInstitutions.length > 1 ? "s" : ""} affiché{filteredInstitutions.length > 1 ? "s" : ""}
            {institutions.length !== filteredInstitutions.length ? ` sur ${institutions.length}` : ""}
          </p>

          {hasActiveFilters && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={resetFilters}
              className="border-[#B8DCDC] text-[#4F8585] hover:bg-[#D4EBEB]"
            >
              <RotateCcw size={15} className="mr-1" />
              Réinitialiser
            </Button>
          )}
        </div>
      </div>

      {filteredInstitutions.length === 0 && (
        <div className="mb-5 rounded-lg border border-dashed border-[#C1DDDD] bg-[#F8FCFC] p-6 text-center text-sm text-gray-500">
          Aucun établissement ne correspond à votre recherche.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredInstitutions.map(inst => (
          <Card key={inst.id || inst._id} className="glass-card border-[#C1DDDD]">
            <CardHeader className="pb-2 flex justify-between items-start">
              <div className="flex items-center space-x-3">
                <div className="bg-[#D4EBEB] p-3 rounded-full"><Building2 className="text-[#4F8585]" size={28} /></div>
                <div>
                  <CardTitle className="text-lg font-semibold text-[#3F7373]">{inst.name}</CardTitle>
                  <p className="text-xs text-gray-500 mt-1 capitalize">{inst.role || "Établissement"}</p>
                </div>
              </div>
              {/* {canManage && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEditModal(inst)} className="border-[#B8DCDC] text-[#4F8585] hover:bg-[#D4EBEB] hover:text-[#294A4A]">
                    <Edit2 size={16} className="mr-1" />Modifier
                  </Button>
                  {inst.status === "pending" && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApprove(inst.id || inst._id)}>Approuver</Button>
                  )}
                </div>
              )} */}
              {canManage && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openEditModal(inst)}
                    className="border-[#B8DCDC] text-[#4F8585] hover:bg-[#D4EBEB] hover:text-[#294A4A]"
                  >
                    <Edit2 size={16} className="mr-1" />
                    Modifier
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(inst)}
                    className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  >
                    <Trash2 size={16} className="mr-1" />
                    Supprimer
                  </Button>

                  {inst.status === "pending" && (
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() =>
                        handleApprove(inst.id || inst._id)
                      }
                    >
                      Approuver
                    </Button>
                  )}
                </div>
              )}
            </CardHeader>

            <CardContent className="space-y-2 text-gray-700 text-sm">
              <div className="flex items-center space-x-2"><Mail size={16} className="text-[#6FAFAF]" /><span>{inst.email || "—"}</span></div>
              <div className="flex items-center space-x-2"><Phone size={16} className="text-[#6FAFAF]" /><span>{inst.phone || "—"}</span></div>
              <div className="flex items-center space-x-2"><MapPin size={16} className="text-[#6FAFAF]" /><span>{inst.address || "Adresse non renseignée"}</span></div>
              <div className="flex items-center space-x-2"><DollarSign size={16} className="text-[#6FAFAF]" /><span>Type de tarification : {inst.type_tarification || "Type de tarification non renseignée"}</span></div>

              <div className="mt-3 border-t border-[#D4EBEB] pt-2">
                <p className="text-sm font-semibold text-[#3F7373] mb-1 flex items-center"><Clock size={16} className="mr-2 text-[#6FAFAF]" />Tarification</p>
                {inst.tarification ? (
                  <div className="grid grid-cols-2 gap-4 ml-2 text-gray-600 text-xs">
                    <div>
                      <h4 className="font-semibold text-[#4F8585] mb-2">Infirmier</h4>
                      {renderTarifs(inst, "infirmier")}
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#4F8585] mb-2">Aide-soignant</h4>
                      {renderTarifs(inst, "aide_soignant")}
                    </div>
                  </div>
                ) : <p className="text-gray-500 italic text-sm">Non définie</p>}
              </div>

              <div className="mt-3">
                <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${inst.status === "approved" ? "bg-green-100 text-green-700" : inst.status === "pending" ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>{inst.status}</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Créé le : {inst.created_at ? new Date(inst.created_at).toLocaleDateString("fr-FR") : "—"}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {editingInst && canManage && (
        <Modal onClose={() => { setEditingInst(null); setIsCreating(false); }}>
          <h2 className="text-xl font-bold mb-4 text-[#3F7373]">{isCreating ? "Créer un nouvel établissement" : "Modifier l'établissement"}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Nom *</Label>
              <Input value={editingInst.name || ""} onChange={e => setEditingInst({ ...editingInst, name: e.target.value })} required />
            </div>
            <div>
              <Label>Email *</Label>
              <Input type="email" value={editingInst.email || ""} onChange={e => setEditingInst({ ...editingInst, email: e.target.value })} required />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input value={editingInst.phone || ""} onChange={e => setEditingInst({ ...editingInst, phone: e.target.value })} />
            </div>
            <div>
              <Label>Adresse</Label>
              <Input value={editingInst.address || ""} onChange={e => setEditingInst({ ...editingInst, address: e.target.value })} />
            </div>
            <div>
              <Label>Type de tarification</Label>
              <Select value={editingInst.type_tarification || "normal"} onValueChange={value => setEditingInst({ ...editingInst, type_tarification: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal (tarif fixe)</SelectItem>
                  <SelectItem value="pourcentage">Pourcentage</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <h3 className="font-semibold text-[#3F7373] mt-6 mb-2">Tarification (€)</h3>
          {isAdmin && <p className="text-xs text-gray-500 mb-3">Les tarifs originels sont visibles uniquement par les administrateurs et ne doivent pas être utilisés dans les calculs des revenus.</p>}
          <div>
            {["infirmier", "aide_soignant"].map(role => (
              <div key={role}>
                <h4 className="font-semibold text-[#4F8585] mb-2 capitalize">{role.replace("_", " ")}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {tarifKeys.map(key => (
                    <div key={role + key} className="rounded-md border border-[#D4EBEB] p-2">
                      <Label className="text-xs">{getTarifLabel(key)}</Label>
                      <Input type="number" step="0.01" min="0" value={editingInst.tarification?.[role]?.[key] ?? ""} onChange={e => updateTarif(role, key, e.target.value)} />
                      {isAdmin && (
                        <div className="mt-2">
                          <Label className="text-xs text-gray-500">{getOriginalLabel(key)}</Label>
                          <Input type="number" step="0.01" min="0" value={editingInst.tarification?.[role]?.[getOriginalKey(key)] ?? ""} onChange={e => updateTarif(role, getOriginalKey(key), e.target.value)} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="outline" onClick={() => { setEditingInst(null); setIsCreating(false); }} className="border-[#B8DCDC] text-[#4F8585] hover:bg-[#D4EBEB]">Annuler</Button>
            <Button onClick={handleSave} disabled={!editingInst.name || !editingInst.email} className="bg-[#6FAFAF] hover:bg-[#4F8585] text-white">{isCreating ? "Créer" : "Enregistrer"}</Button>
          </div>
        </Modal>
      )}
    </Layout>
  );
}